import { useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api";
import { Button } from "../../../components/ui/Button";
import { Search, ChevronLeft, ChevronRight, MoreVertical, Filter, Ban, CalendarDays, PlaneTakeoff, Coins, Link2, Pencil, RotateCcw } from "lucide-react";
import { createPortal } from "react-dom";
import AddCreditModal from "../../../components/AddCreditModal";
import DoctorUnavailabilityModal from "../../../components/DoctorUnavailabilityModal";
import DoctorScheduleModal from "../../../components/DoctorScheduleModal";
import { jwtDecode } from "jwt-decode";
import ShareBookingLinkModal from "../../../components/booking/ShareBookingLinkModal";

export default function Doctor() {
    const [doctors, setDoctors] = useState([]);
  // Only an org or branch admin can create/edit/delete doctor accounts -
  // matches the backend gate in app/routes/actors/doctor.py (_can_manage_doctor).
  const [canManageDoctors, setCanManageDoctors] = useState(false);
  const [isReceptionist, setIsReceptionist] = useState(false);
  const [scheduleDoctor, setScheduleDoctor] = useState(null);
  // Patient booking page for a doctor (booking_link comes with each row).
  const [shareDoctor, setShareDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [specializations, setSpecializations] = useState([]);
  const [specializationFilter, setSpecializationFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [unavailabilityDoctor, setUnavailabilityDoctor] = useState(null);
  const navigate = useNavigate();

  const handleMenuToggle = (event, doctorId) => {
    if (menuOpen === doctorId) {
      setMenuOpen(null);
      return;
    }
    // Get the position of the button relative to the viewport
    const rect = event.currentTarget.getBoundingClientRect();
    const right = Math.max(16, window.innerWidth - rect.right);
    const top = rect.bottom + 4;
    const estimatedHeight = 190;
    const adjustedTop = (top + estimatedHeight > window.innerHeight && rect.top > estimatedHeight)
      ? rect.top - estimatedHeight - 4
      : top;

    setMenuPosition({
      top: adjustedTop,
      right: right
    });
    setMenuOpen(doctorId);
  };

  const addCredit = (doctorId) => {
    setMenuOpen(null); // close menu
    setShowCreditModal(doctorId); // pass doctor id
  };

  const viewSchedule = (doctor) => {
    setMenuOpen(null);
    setScheduleDoctor(doctor);
  };

  const viewUnavailability = (doctor) => {
    setMenuOpen(null); // close menu
    setUnavailabilityDoctor(doctor);
  };

  // Find the selected doctor to pass to the handlers
  const selectedDoctor = doctors.find((s) => s.doctor_id === menuOpen);

  useEffect(() => {
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          const role = decoded.sub?.role?.toLowerCase() || authData.role?.toLowerCase();
          setCanManageDoctors(role === "admin" || role === "branch admin");
          setIsReceptionist(role === "receptionist");
        }
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, []);

    useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await api.fetchDoctors({ page: currentPage, limit: 20, search: searchQuery, specialization: specializationFilter });
        setDoctors(res.data.data || []);
        setTotalPages(res.data.pages || 1);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to load doctors");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentPage, searchQuery, specializationFilter]);

  // Populate the specialization filter dropdown from the live list, same
  // source AppointmentNew.js's own specialization filter already uses.
  useEffect(() => {
    api.fetchSpecializations()
      .then((res) => {
        const list = res.data?.data;
        if (Array.isArray(list)) setSpecializations(list);
      })
      .catch((err) => console.error("Failed to fetch specializations:", err));
  }, []);

  function handleEdit(e, doctor) {
    setMenuOpen(null);
    navigate("/doctor-edit/", { state: doctor });
  }

  const loadDoctors = async () => {
    try {
      const res = await api.fetchDoctors({ page: currentPage, limit: 20, search: searchQuery, specialization: specializationFilter });
      setDoctors(res.data.data || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      setError("Failed to load doctors: " + err);
    } finally {
      setLoading(false);
    }
  };

  // Deactivate/reactivate are both soft toggles (is_active on the doctor +
  // linked user) - there is no hard-delete path for a doctor account at all,
  // so a deactivated clinician stays fully visible here (with an Inactive
  // badge) and can always be brought back rather than being gone for good.
  const handleToggleActive = (e, id, isActive) => {
    (async () => {
      const confirmMsg = isActive
        ? "Deactivate this clinician? They'll stop appearing as bookable, but their account and history are kept and this can be undone anytime."
        : "Reactivate this clinician? They'll become bookable again.";
      if (!window.confirm(confirmMsg)) return;
      try {
        const res = isActive ? await api.cancelDoctor(id) : await api.reactivateDoctor(id);
        if (res.status === 200) {
          alert(isActive ? "Clinician deactivated." : "Clinician reactivated.");
          loadDoctors();
        }
        setMenuOpen(null);
      } catch (err) {
        setError("Failed to update clinician: " + err);
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">Clinicians</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" size={18} />
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearchQuery(searchInput); setCurrentPage(1); } }}
              className="pl-9 pr-4 py-2 w-full sm:w-64 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" size={16} />
            <select
              value={specializationFilter}
              onChange={(e) => { setSpecializationFilter(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-8 py-2 w-full sm:w-56 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white appearance-none"
              title="Filter by Specialization"
            >
              <option value="">All Specializations</option>
              {specializations.map((s) => (
                <option key={s.id || s.name} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          {canManageDoctors && (
            <Link to="/doctor-new" className="text-blue-600 w-full sm:w-auto">
              <button className="w-full sm:w-auto px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors" data-label="New Clinician" data-tour="create-doctor">
                + New Clinician
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-soft rounded-xl overflow-x-auto border border-secondary-100">
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-secondary-500 font-medium">Loading clinicians...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">Error: {error}</p>
            </div>
          ) : doctors.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-secondary-500 font-medium">No clinician found.</p>
            </div>
          ) : (
            <div>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-secondary-200" key="table-header-row">
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">ID</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Clinician Name</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Phone Number</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Specialized</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Exp.</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Degree</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Status</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider text-right pr-6">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {doctors.map((d, index) => (
                    <tr key={d.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">#{(currentPage - 1) * 20 + index + 1}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">
                        <button
                          type="button"
                          onClick={() => viewSchedule(d)}
                          className="text-left hover:text-primary-700 hover:underline"
                          title="View schedule"
                        >
                          {d.name}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{d.phone_number}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">
                        <div>{d.specialized}</div>
                        {d.languages && d.languages.length > 0 && (
                          <div className="text-[11px] text-secondary-500 mt-1 flex flex-wrap gap-1">
                            {d.languages.map((lang) => (
                              <span key={lang} className="bg-secondary-100 text-secondary-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                {lang}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{d.experience}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium max-w-[220px]">
                        <div className="truncate" title={d.degree}>{d.degree}</div>
                        {d.registration_number && (
                          <span className="inline-block mt-0.5 text-[11px] font-mono text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded border border-primary-200">
                            Reg: {d.registration_number}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {d.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-right pr-4 relative">
                        <button
                          className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-600 hover:text-secondary-900 transition-colors cursor-pointer"
                          onClick={(e) => handleMenuToggle(e, d.doctor_id)}
                          data-label="Doctor Options"
                          title="Actions"
                        >
                          <MoreVertical size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              
            </table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200 bg-gray-50">
              <div className="text-sm text-secondary-500 font-medium">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                >
                  <ChevronRight size={16} />
                </Button>
            </div>
            </div>

              {/* The portal for the credit transfer popup */}
              {showCreditModal && (
                <AddCreditModal
                  recpID={showCreditModal}
                  type="st"
                  labelName="Clinician"
                  onClose={() => setShowCreditModal(false)}
                  onSuccess={loadDoctors} // reload after transfer
                />
              )}

              {/* The portal for the context menu */}
              {menuOpen &&
                selectedDoctor &&
                createPortal(
                  <div
                    className="fixed bg-white border border-secondary-200 rounded-xl shadow-xl z-50 min-w-[185px] py-1.5 animate-in fade-in zoom-in-95 duration-100"
                    style={{
                      top: `${menuPosition.top}px`,
                      right: `${menuPosition.right}px`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800"
                      onClick={() => viewSchedule(selectedDoctor)}
                      data-label="View Doctor Schedule"
                    >
                      <CalendarDays size={14} /> View schedule
                    </button>
                    {canManageDoctors && (
                      <button
                        className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800"
                        onClick={(e) => handleEdit(e, selectedDoctor)}
                        data-label="Edit Doctor"
                      >
                        <Pencil size={14} /> Edit
                      </button>
                    )}
                    {selectedDoctor.booking_link && (
                      <button
                        className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800"
                        onClick={() => { setShareDoctor(selectedDoctor); setMenuOpen(null); }}
                        data-label="Share Booking Link"
                      >
                        <Link2 size={14} /> Share booking link
                      </button>
                    )}
                    {!isReceptionist && (
                      <button
                        className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800"
                        onClick={() => addCredit(selectedDoctor.doctor_id)}
                        data-label="Add Credit Doctor"
                      >
                        <Coins size={14} /> Add Credit
                      </button>
                    )}
                    <button
                      className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800"
                      onClick={() => viewUnavailability(selectedDoctor)}
                      data-label="View Doctor Unavailability"
                    >
                      <PlaneTakeoff size={14} /> View Unavailability
                    </button>
                    {canManageDoctors && (
                      <>
                        <div className="border-t border-secondary-100 my-1"></div>
                        <button
                          className={`flex items-center gap-2 w-full text-left px-4 py-2 transition-colors text-sm font-medium ${
                            selectedDoctor.is_active
                              ? "hover:bg-red-50 text-red-600"
                              : "hover:bg-green-50 text-green-700"
                          }`}
                          onClick={(e) => handleToggleActive(e, selectedDoctor.doctor_id, selectedDoctor.is_active)}
                          data-label={selectedDoctor.is_active ? "Deactivate Doctor" : "Reactivate Doctor"}
                        >
                          {selectedDoctor.is_active ? <Ban size={14} /> : <RotateCcw size={14} />} {selectedDoctor.is_active ? "Deactivate" : "Reactivate"}
                        </button>
                      </>
                    )}
                  </div>,
                  document.body
                )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4 mt-4">
        {loading ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-secondary-500 font-medium">Loading doctors...</p>
          </div>
        ) : error ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-red-500">Error: {error}</p>
          </div>
        ) : doctors.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-secondary-500 font-medium">No clinician found.</p>
          </div>
        ) : (
          doctors.map((d, index) => (
            <div
              key={d.id}
              className="bg-white shadow rounded-lg p-4 border border-gray-200"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-xs text-gray-500 font-medium">ID</span>
                  <span className="text-sm font-semibold text-gray-900">#{(currentPage - 1) * 20 + index + 1}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Clinician Name</span>
                  <span className="text-sm text-gray-900 font-medium">{d.name}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Phone Number</span>
                  <span className="text-sm text-gray-900">{d.phone_number}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Specialized</span>
                  <div className="text-right">
                    <span className="text-sm text-gray-900">{d.specialized}</span>
                    {d.languages && d.languages.length > 0 && (
                      <div className="text-[11px] text-secondary-500 mt-0.5 flex flex-wrap justify-end gap-1">
                        {d.languages.map((lang) => (
                          <span key={lang} className="bg-secondary-100 text-secondary-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                            {lang}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Experience</span>
                  <span className="text-sm text-gray-900">{d.experience}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Degree</span>
                  <span className="text-sm text-gray-900 text-right whitespace-pre-line leading-snug">
                    {d.degree}
                    {d.registration_number && (
                      <span className="block text-[11px] font-mono text-primary-600">Reg: {d.registration_number}</span>
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {d.is_active ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="pt-2 border-t space-y-2">
                  <button
                    onClick={() => viewSchedule(d)}
                    className="flex items-center justify-center gap-1.5 w-full text-center border border-primary-300 text-primary-700 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors font-medium"
                    data-label="View Doctor Schedule"
                  >
                    <CalendarDays size={14} /> View schedule
                  </button>
                  {canManageDoctors && (
                    <button
                      onClick={(e) => handleEdit(e, d)}
                      className="flex items-center justify-center gap-1.5 w-full text-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      data-label="Edit Doctor"
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                  {d.booking_link && (
                    <button
                      onClick={() => setShareDoctor(d)}
                      className="flex items-center justify-center gap-1.5 w-full text-center border border-secondary-300 text-secondary-700 px-4 py-2 rounded-lg hover:bg-secondary-50 transition-colors font-medium"
                      data-label="Share Booking Link"
                    >
                      <Link2 size={14} /> Share booking link
                    </button>
                  )}
                  {!isReceptionist && (
                    <button
                      onClick={() => addCredit(d.doctor_id)}
                      className="flex items-center justify-center gap-1.5 w-full text-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                      data-label="Add Credit Doctor"
                    >
                      <Coins size={14} /> Add Credit
                    </button>
                  )}
                  <button
                    onClick={() => viewUnavailability(d)}
                    className="flex items-center justify-center gap-1.5 w-full text-center bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors font-medium"
                    data-label="View Doctor Unavailability"
                  >
                    <PlaneTakeoff size={14} /> View Unavailability
                  </button>
                  {canManageDoctors && (
                    <button
                      onClick={(e) => handleToggleActive(e, d.doctor_id, d.is_active)}
                      className={`flex items-center justify-center gap-1.5 w-full text-center text-white px-4 py-2 rounded-lg transition-colors font-medium ${
                        d.is_active ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
                      }`}
                      data-label={d.is_active ? "Deactivate Doctor" : "Reactivate Doctor"}
                    >
                      {d.is_active ? <Ban size={14} /> : <RotateCcw size={14} />} {d.is_active ? "Deactivate" : "Reactivate"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* The portal for the credit transfer popup */}
      {showCreditModal && (
        <AddCreditModal
          recpID={showCreditModal}
          type="st"
          labelName="Doctor"
          onClose={() => setShowCreditModal(false)}
          onSuccess={loadDoctors}
        />
      )}

      {/* Read-only weekly schedule */}
      {scheduleDoctor && (
        <DoctorScheduleModal
          doctor={scheduleDoctor}
          onClose={() => setScheduleDoctor(null)}
        />
      )}

      {/* Doctor Unavailability viewer */}
      {unavailabilityDoctor && (
        <DoctorUnavailabilityModal
          doctor={unavailabilityDoctor}
          onClose={() => setUnavailabilityDoctor(null)}
        />
      )}

      {/* Click outside to close menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setMenuOpen(null)}
        />
      )}
    
      <div className="md:hidden flex items-center justify-between mt-4 px-2">
        <div className="text-sm text-secondary-500 font-medium">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
      {shareDoctor && (
        <ShareBookingLinkModal link={shareDoctor.booking_link} doctorName={shareDoctor.name}
          doctorUserId={shareDoctor.doctor_id} onClose={() => setShareDoctor(null)} />
      )}
</DashboardLayout>
  );
}
